import React, { useEffect } from "react";
import { X } from "lucide-react";
import { FormDataType, DIRECTIONS, PAYMENT_METHODS, getOperatorFeeRate } from "../types/profitTracking";
import { formatNumber } from "../utils/profitFormatters";

interface ProfitEntryModalProps {
  isOpen: boolean;
  editingEntry: any;
  formData: FormDataType;
  onClose: () => void;
  onChange: (data: Partial<FormDataType>) => void;
  onSave: () => void;
}

export const ProfitEntryModal: React.FC<ProfitEntryModalProps> = ({
  isOpen,
  editingEntry,
  formData,
  onClose,
  onChange,
  onSave,
}) => {
  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-xl overflow-y-auto">
        <div className="flex sticky top-0 justify-between items-center p-6 bg-white border-b">
          <h3 className="text-lg font-semibold">{editingEntry ? "Modifier l'entrée" : "Nouvelle entrée"}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => onChange({ date: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Heure</label>
              <input
                type="text"
                placeholder="ex: 04h47"
                value={formData.heure}
                onChange={(e) => onChange({ heure: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Direction *</label>
              <select
                value={formData.direction}
                onChange={(e) => onChange({ direction: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              >
                {DIRECTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Pays *</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => onChange({ country: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Moyen de paiement *</label>
              <select
                value={formData.payment_method}
                onChange={(e) => onChange({ payment_method: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              >
                {PAYMENT_METHODS.map((pm) => (
                  <option key={pm} value={pm}>
                    {pm}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">
                Capital ({formData.capital_currency}) *
              </label>
              <input
                type="number"
                value={formData.capital_amount || ""}
                onChange={(e) => onChange({ capital_amount: parseFloat(e.target.value) || 0 })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>
          </div>

          {/* USDT Conversion Section */}
          <hr />
          <p className="text-xs font-semibold text-gray-500 uppercase">Conversion USDT</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Plateforme Achat</label>
              <input
                type="text"
                value={formData.buy_platform}
                onChange={(e) => onChange({ buy_platform: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">
                Taux Achat USDT ({formData.capital_currency})
              </label>
              <input
                type="number"
                step="0.0001"
                value={formData.buy_rate || ""}
                onChange={(e) => onChange({ buy_rate: parseFloat(e.target.value) || 0 })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">USDT Obtenus</label>
              <input
                type="number"
                value={formData.usdt_obtained || ""}
                readOnly
                className="px-3 py-2 w-full text-sm rounded-lg border bg-gray-50"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Plateforme Vente</label>
              <input
                type="text"
                value={formData.sell_platform}
                onChange={(e) => onChange({ sell_platform: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">
                Taux Vente USDT ({formData.direction === "CAD_TO_XAF" ? "XAF" : "CAD"})
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.sell_rate || ""}
                onChange={(e) => onChange({ sell_rate: parseFloat(e.target.value) || 0 })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Taux sur IZI</label>
              <input
                type="number"
                step="0.01"
                value={formData.izi_rate || ""}
                onChange={(e) => onChange({ izi_rate: parseFloat(e.target.value) || 0 })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              />
            </div>
          </div>

          {/* Results Section */}
          <hr />
          <p className="text-xs font-semibold text-gray-500 uppercase">Résultats (calculés automatiquement)</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 text-xs text-gray-500">Montant Vente</label>
              <div className="px-3 py-2 text-sm bg-gray-50 rounded-lg border">{formatNumber(formData.sale_amount)}</div>
            </div>

            <div>
              <label className="block mb-1 text-xs text-gray-500">Montant IZI</label>
              <div className="px-3 py-2 text-sm bg-gray-50 rounded-lg border">{formatNumber(formData.izi_amount)}</div>
            </div>

            <div>
              <label className="block mb-1 text-xs text-gray-500">% Bénéfice</label>
              <div className="px-3 py-2 text-sm bg-gray-50 rounded-lg border">
                {formatNumber(formData.profit_percentage, 4)}%
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs text-gray-500">Bénéfice Brut</label>
              <div className="px-3 py-2 text-sm text-green-600 bg-green-50 rounded-lg border">
                {formatNumber(formData.gross_profit)}
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs text-gray-500">
                Frais
                {formData.direction === "CAD_TO_XAF" &&
                  ` ${formData.payment_method} (${formatNumber(getOperatorFeeRate(formData.payment_method) * 100)} %)`}
              </label>
              <div className="px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg border">
                {formatNumber(formData.fees)}
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs text-gray-500">Bénéfice Net</label>
              <div className="px-3 py-2 text-sm font-semibold text-emerald-600 bg-emerald-50 rounded-lg border">
                {formatNumber(formData.net_profit)}
              </div>
            </div>

            <div className="col-span-3">
              <label className="block mb-1 text-xs text-gray-500">Bénéfice en CAD</label>
              <div className="px-3 py-2 text-sm font-bold text-purple-600 bg-purple-50 rounded-lg border">
                {formatNumber(formData.profit_cad, 4)} CAD
              </div>
            </div>
          </div>

          {/* Expenses Section */}
          <hr />
          <p className="text-xs font-semibold text-gray-500 uppercase">Dépenses (optionnel)</p>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Montant dépensé</label>
              <input
                type="number"
                step="0.01"
                value={formData.expense_amount || ""}
                onChange={(e) => onChange({ expense_amount: parseFloat(e.target.value) || 0 })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-medium text-gray-600">Devise</label>
              <select
                value={formData.expense_currency || "XAF"}
                onChange={(e) => onChange({ expense_currency: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
              >
                <option value="CAD">CAD</option>
                <option value="XAF">XAF</option>
                <option value="USD">USD</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block mb-1 text-xs font-medium text-gray-600">Description</label>
              <input
                type="text"
                value={formData.expense_description}
                onChange={(e) => onChange({ expense_description: e.target.value })}
                className="px-3 py-2 w-full text-sm rounded-lg border"
                placeholder="ex: Frais de transfert, achat crédit, etc."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-xs text-gray-500">
                Bénéfice Final ({formData.direction === "CAD_TO_XAF" ? "XAF" : "CAD"})
              </label>
              <div className="px-3 py-2 text-sm font-bold text-emerald-700 bg-emerald-50 rounded-lg border">
                {formatNumber(formData.final_profit)} {formData.direction === "CAD_TO_XAF" ? "XAF" : "CAD"}
              </div>
            </div>

            <div>
              <label className="block mb-1 text-xs text-gray-500">Bénéfice Final (CAD) = Revenu CAD − Dépenses</label>
              <div className="px-3 py-2 text-sm font-bold text-purple-700 bg-purple-50 rounded-lg border">
                {formatNumber(formData.final_profit_cad, 4)} CAD
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-lg border hover:bg-gray-50">
              Annuler
            </button>
            <button
              onClick={onSave}
              className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
